import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Input, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';
import moment from 'moment';
import CardPanel from '../../../shared/components/widgets/CardPanel';

const MAX_NOTE_LENGTH = 2000;

const capitalize = (value) => (value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : '');

const noteBlockStyle = {
  border: '1px solid rgba(128, 128, 128, 0.4)',
  borderRadius: '4px',
  padding: '8px 12px',
  marginBottom: '8px',
  whiteSpace: 'pre-wrap', // keep line breaks; text is rendered as plain text, never HTML
  wordBreak: 'break-word',
};

// Accent colors per retailer so notes can be scanned quickly ('all' and unknown use gray)
const RETAILER_COLORS = {
  amazon: '#ff9900',
  google: '#4285f4',
  meta: '#0866ff',
  all: '#8a8f98',
};
const getRetailerColor = (retailer) => RETAILER_COLORS[retailer] || RETAILER_COLORS.all;

const noteCardStyle = (retailer) => ({
  border: '1px solid rgba(128, 128, 128, 0.3)',
  borderLeft: `4px solid ${getRetailerColor(retailer)}`,
  borderRadius: '4px',
  padding: '10px 14px',
  marginBottom: '10px',
});

const noteHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
  marginBottom: '6px',
};

const noteMetaStyle = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '10px',
  fontSize: '13px',
};

const retailerBadgeStyle = (retailer) => ({
  backgroundColor: getRetailerColor(retailer),
  color: '#fff',
  borderRadius: '10px',
  padding: '1px 10px',
  fontSize: '12px',
  fontWeight: 600,
});

const expiryBadgeStyle = {
  border: '1px solid rgba(128, 128, 128, 0.6)',
  borderRadius: '10px',
  padding: '0 8px',
  fontSize: '12px',
};

// The note itself is the main content: larger text, on its own line, plain text only
const noteTextStyle = {
  fontSize: '15px',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap', // keep line breaks; text is rendered as plain text, never HTML
  wordBreak: 'break-word',
};

/**
 * Shared Budget Notes panel for the Groups / Events / Items tabs (and Multi-Platform).
 * Notes are listed newest first as: [2026-10-05 11:36 - Dan Charbonneau] This is my note
 *
 * @param {Array} notes - Notes from useBudgetNotes
 * @param {boolean} loading - Whether notes are loading
 * @param {Function} addNote - ({ note, retailer, expiresAt }) => Promise<boolean>
 * @param {Function} updateNote - (id, changes) => Promise<boolean>
 * @param {string} defaultRetailer - Retailer a new note is tied to ('all' when unknown/multi)
 */
export default function BudgetNotesPanel({
  notes, loading, addNote, updateNote, defaultRetailer,
}) {
  const [newNote, setNewNote] = useState('');
  const [newRetailer, setNewRetailer] = useState('');
  const [newExpiresAt, setNewExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editExpiresAt, setEditExpiresAt] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [noteToDeactivate, setNoteToDeactivate] = useState(null);

  const retailerForNew = newRetailer || defaultRetailer;
  const retailerChoices = defaultRetailer === 'all' ? ['all'] : [defaultRetailer, 'all'];

  const handleAdd = async () => {
    setSaving(true);
    const ok = await addNote({
      note: newNote,
      retailer: retailerForNew,
      expiresAt: newExpiresAt || undefined,
    });
    setSaving(false);
    if (ok) {
      setNewNote('');
      setNewExpiresAt('');
      setShowCreateModal(false);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditText(note.note);
    setEditExpiresAt(note.expiresAt ? moment(note.expiresAt).format('YYYY-MM-DD') : '');
  };

  const handleSaveEdit = async (note) => {
    // Send only the fields that changed. An empty date clears the expiry (null).
    const originalExpiry = note.expiresAt ? moment(note.expiresAt).format('YYYY-MM-DD') : '';
    const changes = {};
    if (editText !== note.note) {
      changes.note = editText;
    }
    if (editExpiresAt !== originalExpiry) {
      changes.expiresAt = editExpiresAt || null;
    }
    if (Object.keys(changes).length === 0) {
      setEditingId(null);
      return;
    }
    setSaving(true);
    const ok = await updateNote(note.id, changes);
    setSaving(false);
    if (ok) {
      setEditingId(null);
      setEditText('');
      setEditExpiresAt('');
    }
  };

  const handleConfirmDeactivate = async () => {
    setSaving(true);
    await updateNote(noteToDeactivate.id, { isActive: false });
    setSaving(false);
    setNoteToDeactivate(null);
  };

  const formatPrefix = (note) => {
    const name = note.createdBy?.name || 'Unknown';
    return `[${moment(note.createdTs).format('YYYY-MM-DD HH:mm')} - ${name}]`;
  };

  const renderNote = (note) => (
    <div key={note.id} style={noteCardStyle(note.retailer)}>
      {editingId === note.id ? (
        <div>
          <Input
            type="textarea"
            rows={4}
            value={editText}
            maxLength={MAX_NOTE_LENGTH}
            onChange={(e) => setEditText(e.target.value)}
          />
          <div style={{ marginTop: '6px' }}>
            <Input
              type="date"
              style={{ width: 'auto' }}
              value={editExpiresAt}
              onChange={(e) => setEditExpiresAt(e.target.value)}
            />
            <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '6px' }}>
              Optional expire date. Leave blank for no expiry.
            </div>
            <Button
              color="primary"
              size="sm"
              disabled={saving || !editText.trim()}
              onClick={() => handleSaveEdit(note)}
            >
              Save
            </Button>
            <Button color="secondary" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
            <span style={{ fontSize: '12px' }}>{editText.length}/{MAX_NOTE_LENGTH}</span>
          </div>
        </div>
      ) : (
        <div>
          <div style={noteHeaderStyle}>
            <div style={noteMetaStyle}>
              <span style={retailerBadgeStyle(note.retailer)}>{capitalize(note.retailer)}</span>
              <span style={{ fontWeight: 600 }}>{note.createdBy?.name || 'Unknown'}</span>
              <span style={{ opacity: 0.7 }}>{moment(note.createdTs).format('MMM D, YYYY h:mm A')}</span>
              {note.expiresAt && (
                <span style={expiryBadgeStyle}>
                  Expires {moment(note.expiresAt).format('MMM D, YYYY')}
                </span>
              )}
            </div>
            {note.canEdit && (
              <div style={{ whiteSpace: 'nowrap' }}>
                <Button
                  outline
                  color="primary"
                  size="sm"
                  style={{ margin: '0 6px 0 0' }}
                  onClick={() => startEdit(note)}
                >
                  Edit
                </Button>
                <Button
                  outline
                  color="danger"
                  size="sm"
                  style={{ margin: 0 }}
                  onClick={() => setNoteToDeactivate(note)}
                >
                  Deactivate
                </Button>
              </div>
            )}
          </div>
          <div style={noteTextStyle}>{note.note}</div>
        </div>
      )}
    </div>
  );

  return (
    <CardPanel
      title="Budget Notes"
      subhead="Notes for this retailer and all budget levels"
      parentRefresh={loading}
      autorenewButton={false}
    >
      <div>
        <Button
          color="primary"
          style={{ marginBottom: '12px' }}
          onClick={() => setShowCreateModal(true)}
        >
          Add Note
        </Button>
        {!loading && notes.length === 0 && <p>No budget notes yet.</p>}
        {notes.map(renderNote)}

        <Modal isOpen={showCreateModal} toggle={() => setShowCreateModal(false)} size="lg">
          <ModalHeader toggle={() => setShowCreateModal(false)}>Add Budget Note</ModalHeader>
          <ModalBody>
            <Input
              type="textarea"
              rows={6}
              placeholder="Add a budget note..."
              value={newNote}
              maxLength={MAX_NOTE_LENGTH}
              onChange={(e) => setNewNote(e.target.value)}
            />
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>
              {newNote.length}/{MAX_NOTE_LENGTH} - Saved as plain text. HTML tags are removed, and a &lt; followed by a letter is treated as a tag start (&quot;spend &lt; 500&quot; is fine, &quot;a&lt;b&quot; loses text after the &lt;).
            </div>
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', marginBottom: '2px' }}>Retailer</div>
              <Input
                type="select"
                style={{ width: 'auto' }}
                value={retailerForNew}
                onChange={(e) => setNewRetailer(e.target.value)}
              >
                {retailerChoices.map(choice => (
                  <option key={choice} value={choice}>{capitalize(choice)}</option>
                ))}
              </Input>
            </div>
            <div style={{ marginTop: '12px' }}>
              <Input
                type="date"
                style={{ width: 'auto' }}
                value={newExpiresAt}
                onChange={(e) => setNewExpiresAt(e.target.value)}
              />
              <div style={{ fontSize: '12px', opacity: 0.8 }}>
                Optional expire date. Leave blank for no expiry.
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button
              color="primary"
              disabled={saving || !newNote.trim()}
              onClick={handleAdd}
            >
              Save Note
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={!!noteToDeactivate} toggle={() => setNoteToDeactivate(null)}>
          <ModalHeader toggle={() => setNoteToDeactivate(null)}>Deactivate Note</ModalHeader>
          <ModalBody>
            <p>Are you sure you want to deactivate this note? It will no longer be shown.</p>
            {noteToDeactivate && (
              <div style={noteBlockStyle}>
                <strong>{formatPrefix(noteToDeactivate)}</strong>
                {' '}
                {noteToDeactivate.note}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setNoteToDeactivate(null)}>Cancel</Button>
            <Button color="danger" disabled={saving} onClick={handleConfirmDeactivate}>
              Deactivate
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </CardPanel>
  );
}

BudgetNotesPanel.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    retailer: PropTypes.string,
    note: PropTypes.string,
    expiresAt: PropTypes.string,
    createdTs: PropTypes.string,
    canEdit: PropTypes.bool,
    createdBy: PropTypes.shape({ name: PropTypes.string }),
  })),
  loading: PropTypes.bool,
  addNote: PropTypes.func.isRequired,
  updateNote: PropTypes.func.isRequired,
  defaultRetailer: PropTypes.string,
};

BudgetNotesPanel.defaultProps = {
  notes: [],
  loading: false,
  defaultRetailer: 'all',
};

